import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { ArrowRight, Monitor, Code, Layers, Package, Building2, CheckCircle2 } from 'lucide-react';

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const solutions = [
    {
      icon: Monitor,
      title: 'Premium Workspace Hardware',
      description: 'Modern, minimalistic hardware setups with clean aesthetics. No RGB, just elegant productivity-focused equipment.',
    },
    {
      icon: Code,
      title: 'Software Solutions',
      description: 'Custom software implementation, configuration, and optimization to complement your hardware infrastructure.',
    },
    {
      icon: Layers,
      title: 'Complete Room Concepts',
      description: 'Integrated hardware and software environments designed for seamless productivity and user experience.',
    },
    {
      icon: Package,
      title: 'Modular Accessories',
      description: 'Premium, interchangeable components for easy maintenance and standardization across locations.',
    },
  ];

  const industries = [
    { name: 'Hospitality', icon: Building2 },
    { name: 'Business Travel', icon: Building2 },
    { name: 'Co-living Spaces', icon: Building2 },
    { name: 'Premium Guest Rooms', icon: Building2 },
    { name: 'Hybrid Workspaces', icon: Building2 },
    { name: 'Commercial Spaces', icon: Building2 },
  ];

  const process = [
    { step: '01', title: 'Discover Needs', description: 'Understanding your hardware and software requirements' },
    { step: '02', title: 'Define Concept', description: 'Creating tailored solutions that fit your environment' },
    { step: '03', title: 'Deliver Setup', description: 'Professional installation and software configuration' },
    { step: '04', title: 'Scale & Support', description: 'Ongoing assistance and scalable solutions' },
  ];

  return (
    <div className="bg-[#FDFBF7]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1770899621442-24237af4c8b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwZGVzayUyMHNldHVwJTIwY2xlYW4lMjB3b3Jrc3BhY2UlMjBtYWNib29rJTIwbGFwdG9wfGVufDB8fHx8MTc3ODA0MDY0M3ww&ixlib=rb-4.1.0&q=85"
            alt="Modern minimalist workspace"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7]/50 via-[#FDFBF7]/80 to-[#FDFBF7]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1
              className="font-['Outfit'] text-5xl sm:text-6xl tracking-tight leading-none text-[#0A1F38] mb-6"
              data-testid="hero-headline"
            >
              Designed for Work. Built for Experience.
            </h1>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] mb-10 max-w-2xl">
              Premium hardware and software solutions for modern environments. From complete workspace concepts to modular accessories, we create elegant, minimalistic setups that enhance comfort, performance and user experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-[#0A1F38] text-[#FDFBF7] font-['Manrope'] font-medium text-base transition-all duration-300 hover:bg-[#0A1F38]/90 hover:shadow-[0_20px_40px_rgba(10,31,56,0.15)] group"
                data-testid="hero-primary-cta"
              >
                Explore Solutions
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={20} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 rounded-full border border-[#E2E8F0] text-[#0A1F38] bg-white/60 backdrop-blur-sm font-['Manrope'] font-medium text-base transition-all duration-300 hover:border-[#0A1F38] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)]"
                data-testid="hero-secondary-cta"
              >
                Book a Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee Section */}
      <section className="bg-[#F5F3ED] py-6" data-testid="marquee-section">
        <Marquee speed={50} gradient={false}>
          <span className="font-['Manrope'] text-sm tracking-[0.2em] uppercase text-[#0A1F38] mx-12">
            Hardware Solutions
          </span>
          <span className="text-[#C5A05B] mx-4">•</span>
          <span className="font-['Manrope'] text-sm tracking-[0.2em] uppercase text-[#0A1F38] mx-12">
            Software Integration
          </span>
          <span className="text-[#C5A05B] mx-4">•</span>
          <span className="font-['Manrope'] text-sm tracking-[0.2em] uppercase text-[#0A1F38] mx-12">
            Modern Minimalist Design
          </span>
          <span className="text-[#C5A05B] mx-4">•</span>
          <span className="font-['Manrope'] text-sm tracking-[0.2em] uppercase text-[#0A1F38] mx-12">
            Premium Workspaces
          </span>
          <span className="text-[#C5A05B] mx-4">•</span>
          <span className="font-['Manrope'] text-sm tracking-[0.2em] uppercase text-[#0A1F38] mx-12">
            Modular Accessories
          </span>
          <span className="text-[#C5A05B] mx-4">•</span>
          <span className="font-['Manrope'] text-sm tracking-[0.2em] uppercase text-[#0A1F38] mx-12">
            Complete Environments
          </span>
          <span className="text-[#C5A05B] mx-4">•</span>
        </Marquee>
      </section>

      {/* Why This Matters Section */}
      <section className="py-24 sm:py-32" data-testid="value-proposition-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-6">
                More Than Products. Complete Environments.
              </h2>
              <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] mb-8">
                Most spaces aren't built for real work, comfort, or performance. Poor ergonomics, inadequate hardware, and disconnected software create friction for users who expect modern, premium experiences.
              </p>
              <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569]">
                We design scalable hardware and software concepts with clean, minimalistic aesthetics—no RGB, no clutter. Just elegant, productive environments people actually want to use.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1570305109029-a0059a91f8f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwZGVzayUyMHNldHVwJTIwY2xlYW4lMjB3b3Jrc3BhY2UlMjBtYWNib29rJTIwbGFwdG9wfGVufDB8fHx8MTc3ODA0MDY0M3ww&ixlib=rb-4.1.0&q=85"
                alt="Clean minimalist workspace"
                className="rounded-3xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-24 sm:py-32 bg-[#F5F3ED]" data-testid="solutions-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-4">
              Our Solutions
            </h2>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] max-w-2xl mx-auto">
              Premium hardware and software tailored to your environment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 sm:p-12 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-[#E2E8F0]/50 transition-all duration-300 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] group"
                data-testid={`solution-card-${index}`}
              >
                <solution.icon
                  className="text-[#C5A05B] mb-6 transition-transform duration-300 group-hover:scale-110"
                  size={48}
                  strokeWidth={1.5}
                />
                <h3 className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight text-[#0A1F38] mb-4">
                  {solution.title}
                </h3>
                <p className="font-['Manrope'] text-base leading-relaxed text-[#475569]">
                  {solution.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Insight */}
      <section className="py-24 sm:py-32" data-testid="founder-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-[#0A1F38] rounded-3xl p-10 sm:p-16 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#C5A05B]/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <div className="w-16 h-1 bg-[#C5A05B] mb-8"></div>
              <blockquote className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight leading-relaxed text-[#F8FAFC] mb-6">
                "I'm not just selling this solution — I'm also the customer who wants to book it."
              </blockquote>
              <p className="font-['Manrope'] text-base leading-relaxed text-[#94A3B8]">
                With 80+ nights per year in hotels and guest environments, I understand the frustration of poor workspaces, bad ergonomics, and missing equipment. This real-world experience drives every hardware and software solution we create.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-24 sm:py-32 bg-[#F5F3ED]" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-4">
              Where We Deliver Value
            </h2>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] max-w-2xl mx-auto">
              From hospitality to hybrid workspaces—our solutions fit diverse environments
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white rounded-2xl p-6 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-[#E2E8F0]/50 transition-all duration-300 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]"
                data-testid={`industry-card-${index}`}
              >
                <industry.icon className="text-[#C5A05B] mx-auto mb-3" size={32} strokeWidth={1.5} />
                <h4 className="font-['Manrope'] text-base font-semibold text-[#0A1F38]">{industry.name}</h4>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 sm:py-32" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-4">
              How We Work
            </h2>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] max-w-2xl mx-auto">
              A streamlined process from concept to delivery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
                data-testid={`process-step-${index}`}
              >
                <div className="mb-4">
                  <span className="font-['Outfit'] text-6xl font-bold text-[#C5A05B]/20">{item.step}</span>
                </div>
                <h3 className="font-['Outfit'] text-xl sm:text-2xl font-medium text-[#0A1F38] mb-3">
                  {item.title}
                </h3>
                <p className="font-['Manrope'] text-base leading-relaxed text-[#475569]">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Modular Accessories Teaser */}
      <section className="py-24 sm:py-32 bg-[#F5F3ED]" data-testid="accessories-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.pexels.com/photos/4006123/pexels-photo-4006123.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Minimalist workspace accessories"
                className="rounded-3xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] w-full"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-6">
                Modular. Scalable. Easy to Maintain.
              </h2>
              <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] mb-6">
                Our modular accessories system makes it simple to standardize, replenish, and scale your setups. Premium quality components with minimalist design for maximum productivity.
              </p>
              <ul className="space-y-3 mb-8">
                {['Easy replenishment', 'Standardized kits', 'Premium quality', 'Low maintenance'].map(
                  (feature, idx) => (
                    <li key={idx} className="flex items-center space-x-3">
                      <CheckCircle2 className="text-[#C5A05B]" size={20} />
                      <span className="font-['Manrope'] text-base text-[#475569]">{feature}</span>
                    </li>
                  )
                )}
              </ul>
              <Link
                to="/products"
                className="inline-flex items-center px-8 py-4 rounded-full bg-[#0A1F38] text-[#FDFBF7] font-['Manrope'] font-medium text-base transition-all duration-300 hover:bg-[#0A1F38]/90 hover:shadow-[0_20px_40px_rgba(10,31,56,0.15)] group"
                data-testid="accessories-cta"
              >
                View Products
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={20} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 sm:py-32" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-6">
              Ready to Transform Your Environment?
            </h2>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] mb-10 max-w-2xl mx-auto">
              Let's discuss how we can create premium hardware and software solutions tailored to your needs.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-10 py-5 rounded-full bg-[#0A1F38] text-[#FDFBF7] font-['Manrope'] font-semibold text-lg transition-all duration-300 hover:bg-[#0A1F38]/90 hover:shadow-[0_20px_40px_rgba(10,31,56,0.15)] group"
              data-testid="final-cta-button"
            >
              Get in Touch
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={24} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;